/**
 * 地形战斗系统 - 根据地形类型对不同兵种进行属性修正
 */

import type { TerrainType, UnitType, FactionId } from '@/types';
import { FACTION_TERRAIN } from '@/types';

/**
 * 单位在特定地形的属性修正
 */
export interface TerrainModifier {
  attack: number;      // 攻击修正 (1.0 = 100%, 1.25 = +25%)
  defense: number;     // 防御修正
  speed: number;       // 速度修正
  // 特殊效果
  canCharge?: boolean;       // 是否可以冲锋
  chargeBonus?: number;      // 冲锋伤害加成
  critChance?: number;       // 暴击率加成
  rangeBonus?: number;       // 射程加成
  dotDamage?: number;        // 持续伤害(每秒)
  dotDuration?: number;      // 持续伤害时长
  stunChance?: number;       // 眩晕几率
  dodgeChance?: number;      // 闪避几率
}

/**
 * 默认修正（无加成无减益）
 */
const DEFAULT_MODIFIER: TerrainModifier = {
  attack: 1.0,
  defense: 1.0,
  speed: 1.0,
  canCharge: true,
  chargeBonus: 1.0,
};

/**
 * 地形-兵种修正表
 * terrain -> unitType -> modifier
 */
export const TERRAIN_UNIT_MODIFIERS: Record<TerrainType, Record<UnitType, TerrainModifier>> = {
  // 平原 - 中性地形
  plain: {
    sword: { attack: 1.0, defense: 1.0, speed: 1.0 },
    archer: { attack: 1.0, defense: 1.0, speed: 1.0 },
    spear: { attack: 1.0, defense: 1.0, speed: 1.0 },
    cavalry: { attack: 1.05, defense: 1.0, speed: 1.05, canCharge: true, chargeBonus: 1.1 },
    shield: { attack: 1.0, defense: 1.0, speed: 1.0 },
    taoist: { attack: 1.0, defense: 1.0, speed: 1.0 },
  },

  // 草原 - 云国特色，骑兵天堂
  grassland: {
    sword: { attack: 1.0, defense: 0.9, speed: 1.0 },
    archer: { attack: 1.1, defense: 0.95, speed: 1.05, critChance: 0.15 },
    spear: { attack: 1.15, defense: 1.0, speed: 1.0 }, // 对骑兵克制+25%在战斗系统处理
    cavalry: { attack: 1.25, defense: 1.0, speed: 1.35, canCharge: true, chargeBonus: 1.5 },
    shield: { attack: 1.0, defense: 0.8, speed: 0.9 },
    taoist: { attack: 1.0, defense: 0.9, speed: 1.0 },
  },

  // 森林 - 林国特色，伏击者乐园
  forest: {
    sword: { attack: 1.1, defense: 1.1, speed: 0.95, critChance: 0.2 },
    archer: { attack: 1.3, defense: 1.2, speed: 0.9, critChance: 0.25 },
    spear: { attack: 1.05, defense: 1.05, speed: 0.85 },
    cavalry: { attack: 0.75, defense: 0.85, speed: 0.55, canCharge: false, stunChance: 0.2 },
    shield: { attack: 1.0, defense: 1.15, speed: 0.9 },
    taoist: { attack: 1.15, defense: 1.15, speed: 0.95 },
  },

  // 雪原 - 寒国特色，冰天雪地
  snow: {
    sword: { attack: 0.95, defense: 1.05, speed: 0.8, dotDamage: 1, dotDuration: 3 },
    archer: { attack: 0.85, defense: 1.0, speed: 0.85, critChance: -0.2 },
    spear: { attack: 1.0, defense: 1.1, speed: 0.8 },
    cavalry: { attack: 0.8, defense: 0.9, speed: 0.65, canCharge: false },
    shield: { attack: 1.0, defense: 1.2, speed: 0.85 },
    taoist: { attack: 1.1, defense: 1.05, speed: 0.9, stunChance: 0.25 },
  },

  // 火山 - 炎国特色，熔岩地带
  volcano: {
    sword: { attack: 1.15, defense: 0.9, speed: 1.0, dotDamage: 2, dotDuration: 4 },
    archer: { attack: 1.2, defense: 0.85, speed: 1.0, dotDamage: 2, dotDuration: 4 },
    spear: { attack: 1.1, defense: 0.9, speed: 0.95 },
    cavalry: { attack: 0.9, defense: 0.8, speed: 0.75, canCharge: false },
    shield: { attack: 1.0, defense: 0.75, speed: 0.9, dotDamage: 1, dotDuration: 3 },
    taoist: { attack: 1.25, defense: 1.0, speed: 1.0, rangeBonus: 1.3 },
  },

  // 山丘 - 铁国特色，易守难攻
  highland: {
    sword: { attack: 1.05, defense: 1.15, speed: 0.85 },
    archer: { attack: 1.25, defense: 1.1, speed: 0.9, rangeBonus: 1.3, critChance: 0.2 },
    spear: { attack: 1.1, defense: 1.25, speed: 0.85 },
    cavalry: { attack: 0.85, defense: 0.9, speed: 0.6, canCharge: false },
    shield: { attack: 1.05, defense: 1.3, speed: 0.8 },
    taoist: { attack: 1.0, defense: 1.1, speed: 0.9 },
  },

  // 沼泽 - 玄国特色，瘴气弥漫
  swamp: {
    sword: { attack: 1.0, defense: 0.9, speed: 0.8, stunChance: 0.1 },
    archer: { attack: 1.05, defense: 0.95, speed: 0.85, dotDamage: 1, dotDuration: 3 },
    spear: { attack: 0.9, defense: 0.85, speed: 0.75 },
    cavalry: { attack: 0.65, defense: 0.75, speed: 0.45, canCharge: false, stunChance: 0.15 },
    shield: { attack: 0.85, defense: 0.8, speed: 0.65 },
    taoist: { attack: 1.3, defense: 1.1, speed: 0.95, dotDamage: 3, dotDuration: 6 },
  },

  // 荒漠 - 商国特色，风沙肆虐
  desert: {
    sword: { attack: 1.0, defense: 0.95, speed: 0.85 },
    archer: { attack: 0.8, defense: 0.9, speed: 0.9, critChance: -0.25 },
    spear: { attack: 1.0, defense: 1.0, speed: 0.85 },
    cavalry: { attack: 0.85, defense: 0.85, speed: 0.65, canCharge: false },
    shield: { attack: 1.0, defense: 0.85, speed: 0.8 },
    taoist: { attack: 1.15, defense: 1.0, speed: 1.0, stunChance: 0.2 },
  },

  // 水域 - 不可通行，但保留定义
  water: {
    sword: { attack: 0.5, defense: 0.5, speed: 0.3 },
    archer: { attack: 0.5, defense: 0.5, speed: 0.3 },
    spear: { attack: 0.5, defense: 0.5, speed: 0.3 },
    cavalry: { attack: 0.3, defense: 0.3, speed: 0.1 },
    shield: { attack: 0.5, defense: 0.5, speed: 0.2 },
    taoist: { attack: 0.5, defense: 0.5, speed: 0.3 },
  },
};

/**
 * 国家在自己特色地形的额外加成
 */
export const FACTION_HOME_TERRAIN_BONUS: Record<FactionId, {
  allStats: number;      // 全属性加成
  specialBonus: string;  // 特殊效果描述
  specialValue: number;  // 特殊效果数值
}> = {
  yan: { allStats: 1.15, specialBonus: 'burnDamage', specialValue: 2.0 },      // 灼烧+100%
  han: { allStats: 1.15, specialBonus: 'freezeChance', specialValue: 0.25 },   // 冰封+25%
  lin: { allStats: 1.15, specialBonus: 'ambushDamage', specialValue: 1.5 },    // 伏击+50%
  shang: { allStats: 1.15, specialBonus: 'dodgeChance', specialValue: 0.15 },  // 沙遁+15%
  xuan: { allStats: 1.15, specialBonus: 'poisonDuration', specialValue: 3 },   // 剧毒+3秒
  tie: { allStats: 1.15, specialBonus: 'armor', specialValue: 20 },            // 护甲+20
  yun: { allStats: 1.15, specialBonus: 'trampleDamage', specialValue: 1.5 },   // 践踏+50%
  neutral: { allStats: 1.0, specialBonus: 'none', specialValue: 0 },
};

/**
 * 获取单位在特定地形的修正
 */
export function getTerrainModifier(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): TerrainModifier {
  const baseModifier = TERRAIN_UNIT_MODIFIERS[terrain]?.[unitType] || DEFAULT_MODIFIER;

  // 复制一份避免修改原数据
  const modifier: TerrainModifier = { ...DEFAULT_MODIFIER, ...baseModifier };

  // 检查是否是国家的特色地形
  if (factionId && factionId !== 'neutral') {
    const factionTerrain = FACTION_TERRAIN[factionId];

    // 如果在自己的特色地形，应用额外加成
    if (factionTerrain === terrain) {
      const homeBonus = FACTION_HOME_TERRAIN_BONUS[factionId];
      modifier.attack *= homeBonus.allStats;
      modifier.defense *= homeBonus.allStats;
      modifier.speed = Math.max(modifier.speed, 1.0); // 本国地形不受速度惩罚

      // 应用特殊加成
      switch (homeBonus.specialBonus) {
        case 'dodgeChance':
          modifier.dodgeChance = (modifier.dodgeChance || 0) + homeBonus.specialValue;
          break;
        case 'freezeChance':
          modifier.stunChance = (modifier.stunChance || 0) + homeBonus.specialValue;
          break;
      }
    }

    // 国家免疫自己地形的负面效果
    if (factionTerrain === terrain) {
      // 移除持续伤害
      delete modifier.dotDamage;
      delete modifier.dotDuration;
      // 移除眩晕（负面）
      if (modifier.stunChance && modifier.stunChance < 0) {
        delete modifier.stunChance;
      }
    }
  }

  return modifier;
}

/**
 * 计算地形对攻击的最终修正
 */
export function getTerrainAttackModifier(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.attack;
}

/**
 * 计算地形对防御的最终修正
 */
export function getTerrainDefenseModifier(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.defense;
}

/**
 * 计算地形对速度的最终修正
 */
export function getTerrainSpeedModifier(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.speed;
}

/**
 * 检查单位是否可以在该地形冲锋
 */
export function canChargeOnTerrain(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): boolean {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.canCharge !== false;
}

/**
 * 获取冲锋伤害加成
 */
export function getChargeBonus(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.chargeBonus || 1.0;
}

/**
 * 获取暴击率加成
 */
export function getCritChanceBonus(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.critChance || 0;
}

/**
 * 获取射程加成
 */
export function getRangeBonus(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.rangeBonus || 1.0;
}

/**
 * 获取闪避几率
 */
export function getDodgeChance(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.dodgeChance || 0;
}

/**
 * 获取眩晕几率
 */
export function getStunChance(
  terrain: TerrainType,
  unitType: UnitType,
  factionId?: FactionId
): number {
  const modifier = getTerrainModifier(terrain, unitType, factionId);
  return modifier.stunChance || 0;
}
